/**
 * Map position sharing — PIXI-native sprites on the viewport.
 *
 * Live users:    PerfectCursor interpolates 50ms WS position updates.
 * Firefly replays: setInterval + eased lerp from backend trail data.
 *
 * Click/tap: sprites use eventMode='static' + pointertap (same as quest markers).
 * Hit area is updated every 200ms to always equal HIT_RADIUS_SCREEN_PX in screen pixels.
 *
 * Firefly state persists across React unmount/remount (map ↔ tapscreen navigation).
 */

import { useEffect, useRef } from 'react';
import { useLatest } from './useLatest';
import { PerfectCursor } from 'perfect-cursors';
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import {
  fireflyAckEndpointDebugFireflyAckPost,
  getCursorUserInfoCursorUserInfoTgUserIdGet,
} from '../client';
import { boundedSet } from '../utils/boundedMap';

const WORLD_SIZE       = 7500;
const SEND_INTERVAL_MS = 50;
const MOVE_THRESHOLD   = 0.0003;
const REPLAY_TICK_MS   = 100;
const HIT_RADIUS_SCREEN_PX = 22; // screen pixels — reduced to avoid blocking buildings

// Firefly visual constants
const FIREFLY_COLORS = [0xffcc33, 0x44ffaa, 0xff7799, 0x44ccff, 0xff9944, 0x99ff44, 0xcc88ff, 0xff5544];
const FIREFLY_OUTER_R = 18; // world units — outer glow ring
const FIREFLY_CORE_R  = 4;  // world units — bright core
const TRAIL_LENGTH    = 14; // number of history positions kept for the comet tail

// ── Module-level firefly state — persists across React component lifecycle ─────
type Pt = { x: number; y: number; t: number };
type FireflyReplayState = {
  fireflyId:  string;
  color:      number;
  points:     Pt[];
  total:      number;     // last point's t (seconds)
  startedAt:  number;     // Date.now() when replay first started
  phase:      number;     // pulse phase offset (0–2π)
  recordedAt: number | null; // unix timestamp of the last trail point
};
const _fireflies = {
  replays:   new Map<string, FireflyReplayState>(),
  intervals: new Map<string, ReturnType<typeof setInterval>>(),
  sprites:   new Map<string, PIXI.Container>(),
  trails:    new Map<string, PIXI.Graphics>(),
  histories: new Map<string, Array<{ x: number; y: number }>>(),
};

// Level-based scaling cache. Module-scoped on purpose: it must survive the
// component unmount that happens on every map ⇄ tap navigation, so it can't
// live in React state. That means nothing frees it on its own, so it's bounded
// FIFO — a level never changes more than once per session, so plain
// insertion-order eviction (Map keeps insertion order) is enough; no LRU.
const MAX_LEVEL_CACHE = 500; // hard ceiling on simultaneously-visible cursors on the map
const _levelCache  = new Map<string, number>();
const _pendingFetch = new Set<string>();

const cacheLevel = (uid: string, lvl: number) =>
  boundedSet(_levelCache, uid, lvl, MAX_LEVEL_CACHE);

// ── Pure helpers ──────────────────────────────────────────────────────────────

function getFireflyColor(uid: string): number {
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  return FIREFLY_COLORS[h % FIREFLY_COLORS.length];
}

function normToWorld(nx: number, ny: number) {
  return { x: (nx - 0.5) * WORLD_SIZE, y: (ny - 0.5) * WORLD_SIZE };
}

function getWsBase(): string {
  const o = window.location.origin;
  if (o.includes('localhost')) return 'ws://127.0.0.1:8001';
  if (o.includes('ngrok-free.app')) return o.replace('https://', 'wss://');
  return 'wss://notapi.web3islands.com';
}

function resolveLocalUserId(): string | null {
  try {
    const id = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
    if (id) return String(id);
  } catch { /* ignore */ }
  try {
    const v = localStorage.getItem('test_user_id');
    if (v) return v;
  } catch { /* ignore */ }
  return null;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

/** Remove stationary points, keep real timestamps for accurate speed replay. */
function optimizeReplay(raw: Pt[]): Pt[] {
  if (raw.length < 2) return raw;
  const STATIC_THRESHOLD = 0.003;
  const kept: Pt[] = [raw[0]];
  for (const p of raw.slice(1)) {
    const prev = kept[kept.length - 1];
    if (Math.hypot(p.x - prev.x, p.y - prev.y) >= STATIC_THRESHOLD) kept.push(p);
  }
  return kept.length >= 2 ? kept : raw;
}

function getReplayWorldPos(points: Pt[], elapsed: number): { x: number; y: number } {
  let prev = points[0], next = points[points.length - 1];
  for (let i = 0; i < points.length - 1; i++) {
    if (points[i].t <= elapsed && points[i + 1].t > elapsed) {
      prev = points[i]; next = points[i + 1]; break;
    }
  }
  const seg = next.t - prev.t;
  const t   = seg > 0 ? easeInOut(Math.min((elapsed - prev.t) / seg, 1)) : 1;
  return normToWorld(prev.x + (next.x - prev.x) * t, prev.y + (next.y - prev.y) * t);
}

/** scale = 1 + 2 * ((level-1)/99)² → level1=1.0×, level50≈1.49×, level100=3.0× */
function levelToScale(level: number): number {
  const l = Math.max(1, Math.min(100, level));
  return 1 + 2 * ((l - 1) / 99) ** 2;
}

/**
 * Draw a comet-style tail on a PIXI.Graphics layer.
 * Interpolates between history positions so the tail is always
 * continuous — no gaps even when the firefly moves fast.
 */
function drawCometTail(
  g: PIXI.Graphics,
  hist: Array<{ x: number; y: number }>,
  color: number,
  baseR: number,
): void {
  g.clear();
  if (hist.length < 2) return;

  // Build a dense path: insert intermediate points so no gap exceeds baseR.
  // This guarantees adjacent circles always overlap → solid comet effect.
  const dense: Array<{ x: number; y: number; pathPos: number }> = [
    { x: hist[0].x, y: hist[0].y, pathPos: 0 },
  ];
  let pathLen = 0;
  for (let i = 1; i < hist.length; i++) {
    const dx = hist[i].x - hist[i - 1].x;
    const dy = hist[i].y - hist[i - 1].y;
    const segLen = Math.hypot(dx, dy);
    const steps = Math.min(Math.ceil(segLen / (baseR * 0.9)), 24); // cap at 24 per seg
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      dense.push({
        x: hist[i - 1].x + dx * t,
        y: hist[i - 1].y + dy * t,
        pathPos: pathLen + segLen * t,
      });
    }
    pathLen += segLen;
  }

  const N = dense.length;
  for (let i = 0; i < N; i++) {
    const ratio = N > 1 ? i / (N - 1) : 1; // 0 = far tail, 1 = head
    const r = baseR * (0.15 + ratio * 1.45); // grows from 0.15× at tail to 1.6× at head
    if (r >= 0.4) {
      g.circle(dense[i].x, dense[i].y, r).fill({ color, alpha: 0.06 + ratio * 0.34 });
    }
  }
}

// ── Firefly graphics builder ──────────────────────────────────────────────────

function buildFireflyGraphics(color: number, scale = 1.0): PIXI.Graphics {
  const outerR = FIREFLY_OUTER_R * scale;
  const coreR  = FIREFLY_CORE_R  * scale;
  const g = new PIXI.Graphics();
  g.circle(0, 0, outerR).fill({ color, alpha: 0.07 });
  g.circle(0, 0, outerR * 0.78).fill({ color, alpha: 0.13 });
  g.circle(0, 0, outerR * 0.56).fill({ color, alpha: 0.25 });
  g.circle(0, 0, outerR * 0.38).fill({ color, alpha: 0.45 });
  g.circle(0, 0, coreR).fill({ color, alpha: 0.95 });
  g.circle(0, 0, coreR * 0.5).fill({ color: 0xffffff, alpha: 0.9 });
  return g;
}

/** Replace the Graphics child in a Container with a new one at the given scale. */
function updateSpriteScale(sprite: PIXI.Container, color: number, scale: number) {
  if (sprite.destroyed) return;
  const old = sprite.getChildAt(0);
  if (old) { sprite.removeChildAt(0); (old as PIXI.Graphics).destroy(); }
  sprite.addChildAt(buildFireflyGraphics(color, scale), 0);
}

/** Fetch level for uid, update sprite scale when received. */
function fetchAndApplyLevel(uid: string, sprite: PIXI.Container, color: number) {
  if (_levelCache.has(uid)) {
    updateSpriteScale(sprite, color, levelToScale(_levelCache.get(uid)!));
    return;
  }
  if (_pendingFetch.has(uid)) return;
  _pendingFetch.add(uid);
  getCursorUserInfoCursorUserInfoTgUserIdGet({
    path: { tg_user_id: Number(uid) },
    throwOnError: true,
  })
    .then(({ data }) => {
      const lvl = data.level ?? 1;
      cacheLevel(uid, lvl);
      _pendingFetch.delete(uid);
      if (!sprite.destroyed) updateSpriteScale(sprite, color, levelToScale(lvl));
    })
    .catch(() => { _pendingFetch.delete(uid); });
}

// ── Hook ──────────────────────────────────────────────────────────────────────

type LiveEntry = { sprite: PIXI.Container; pc: PerfectCursor; trail: PIXI.Graphics; hist: Array<{x:number;y:number}>; lastTrailT: number };

export function useViewportSharing(
  viewportRef: React.RefObject<Viewport | null>,
  userId: string | number | null | undefined,
  roomId = 'main',
  onLiveCursorClick?: (uid: string) => void,
  onFireflyCursorTap?: (fireflyId: string, recordedAt: number | null) => void,
) {
  const wsRef       = useRef<WebSocket | null>(null);
  const liveRef     = useRef<Map<string, LiveEntry>>(new Map());
  const lastSentRef = useRef({ nx: -1, ny: -1 });
  const wsGenRef    = useRef(0);

  const onClickRef   = useLatest(onLiveCursorClick);
  const onFireflyRef = useLatest(onFireflyCursorTap);

  // ── Main effect ────────────────────────────────────────────────────────────
  useEffect(() => {
    const effectiveId = userId != null && userId !== '' ? userId : resolveLocalUserId();
    if (!effectiveId) return;

    // Same Map instance for the hook lifetime; capture for cleanup (react-hooks).
    const live = liveRef.current;
    const gen = ++wsGenRef.current;
    const uid = String(effectiveId);

    // Returns the current hit radius in world units, scaled to HIT_RADIUS_SCREEN_PX screen px
    function hitRadiusWorld(): number {
      const scale = viewportRef.current?.scale.x ?? 1;
      return Math.max(FIREFLY_OUTER_R, HIT_RADIUS_SCREEN_PX / scale);
    }

    // ── Firefly helpers ───────────────────────────────────────────────────

    function makeFireflySprite(
      fireflyId: string,
      color: number,
      pos: { x: number; y: number },
    ): PIXI.Container | null {
      const vp = viewportRef.current;
      if (!vp) return null;

      // Trail Graphics sits BELOW the sprite — also clickable
      const trail = new PIXI.Graphics();
      trail.eventMode = 'static';
      trail.cursor = 'pointer';
      trail.on('pointertap', () => {
        const state = _fireflies.replays.get(fireflyId);
        onFireflyRef.current?.(fireflyId, state?.recordedAt ?? null);
      });
      vp.addChild(trail);
      _fireflies.trails.set(fireflyId, trail);
      _fireflies.histories.set(fireflyId, []);

      const c = new PIXI.Container();
      c.addChild(buildFireflyGraphics(color));
      c.alpha = 0.55;
      c.eventMode = 'static';
      c.hitArea = new PIXI.Circle(0, 0, hitRadiusWorld());
      c.cursor = 'pointer';
      c.on('pointertap', () => {
        const state = _fireflies.replays.get(fireflyId);
        onFireflyRef.current?.(fireflyId, state?.recordedAt ?? null);
      });
      c.position.set(pos.x, pos.y);
      vp.addChild(c);
      fetchAndApplyLevel(fireflyId, c, color);
      return c;
    }

    function destroyFirefly(fireflyId: string) {
      const iid = _fireflies.intervals.get(fireflyId);
      if (iid != null) { clearInterval(iid); _fireflies.intervals.delete(fireflyId); }
      const sprite = _fireflies.sprites.get(fireflyId);
      if (sprite) { sprite.parent?.removeChild(sprite); sprite.destroy(); _fireflies.sprites.delete(fireflyId); }
      const trail = _fireflies.trails.get(fireflyId);
      if (trail) { trail.parent?.removeChild(trail); trail.destroy(); _fireflies.trails.delete(fireflyId); }
      _fireflies.histories.delete(fireflyId);
    }

    function startFireflyInterval(state: FireflyReplayState, startElapsed: number) {
      if (_fireflies.intervals.has(state.fireflyId)) return;
      const t0 = Date.now() - startElapsed * 1000;
      const iid = setInterval(() => {
        if (liveRef.current.has(state.fireflyId)) {
          destroyFirefly(state.fireflyId);
          _fireflies.replays.delete(state.fireflyId);
          return;
        }
        const elapsed = (Date.now() - t0) / 1000;
        if (elapsed > state.total + 3) {
          destroyFirefly(state.fireflyId);
          _fireflies.replays.delete(state.fireflyId);
          return;
        }
        const w = getReplayWorldPos(state.points, elapsed);
        const sprite = _fireflies.sprites.get(state.fireflyId);
        if (sprite) {
          sprite.position.set(w.x, w.y);
          // Pulse alpha for firefly feel
          const pulse = 0.5 + 0.5 * Math.sin((Date.now() / 700) + state.phase);
          sprite.alpha = 0.35 + pulse * 0.35;
          // Keep hitArea in sync with current zoom
          sprite.hitArea = new PIXI.Circle(0, 0, hitRadiusWorld());
        }

        // ── Comet tail ──────────────────────────────────────────────────
        const hist = _fireflies.histories.get(state.fireflyId);
        if (hist != null) {
          hist.push(w);
          if (hist.length > TRAIL_LENGTH) hist.shift();
          const tail = _fireflies.trails.get(state.fireflyId);
          if (tail) {
            const lvl = _levelCache.get(state.fireflyId) ?? 1;
            drawCometTail(tail, hist, state.color, FIREFLY_OUTER_R * levelToScale(lvl) * 0.28);
          }
        }
      }, REPLAY_TICK_MS);
      _fireflies.intervals.set(state.fireflyId, iid);
    }

    function mountFireflySprites() {
      const now = Date.now();
      for (const state of Array.from(_fireflies.replays.values())) {
        if (liveRef.current.has(state.fireflyId)) {
          destroyFirefly(state.fireflyId);
          _fireflies.replays.delete(state.fireflyId);
          continue;
        }
        const elapsed = (now - state.startedAt) / 1000;
        if (elapsed > state.total + 3) {
          _fireflies.replays.delete(state.fireflyId);
          _fireflies.intervals.delete(state.fireflyId);
          continue;
        }
        if (!_fireflies.sprites.has(state.fireflyId)) {
          const pos = getReplayWorldPos(state.points, elapsed);
          const sprite = makeFireflySprite(state.fireflyId, state.color, pos);
          if (sprite) _fireflies.sprites.set(state.fireflyId, sprite);
        }
        startFireflyInterval(state, elapsed);
      }
    }

    function startReplay(fireflyId: string, rawPoints: Pt[], serverElapsed = 0, recordedAt: number | null = null): boolean {
      if (_fireflies.replays.has(fireflyId)) return false;
      if (liveRef.current.has(fireflyId))   return false;

      const points = optimizeReplay(rawPoints);
      if (points.length < 2) return false;

      const total = points[points.length - 1].t;
      const startedAt = Date.now() - serverElapsed * 1000;
      const color = getFireflyColor(fireflyId);
      const phase = (Date.now() % 6283) / 1000;
      const state: FireflyReplayState = { fireflyId, color, points, total, startedAt, phase, recordedAt };
      _fireflies.replays.set(fireflyId, state);

      const pos = getReplayWorldPos(points, serverElapsed);
      const sprite = makeFireflySprite(fireflyId, color, pos);
      if (sprite) _fireflies.sprites.set(fireflyId, sprite);
      startFireflyInterval(state, serverElapsed);
      return true;
    }

    // ── Live cursor helpers ───────────────────────────────────────────────

    function makeLiveSprite(liveId: string, color: number): { sprite: PIXI.Container; trail: PIXI.Graphics } | null {
      const vp = viewportRef.current;
      if (!vp) return null;
      const trail = new PIXI.Graphics();
      vp.addChild(trail);
      const c = new PIXI.Container();
      c.addChild(buildFireflyGraphics(color));
      c.alpha = 1.0;
      c.eventMode = 'static';
      c.hitArea = new PIXI.Circle(0, 0, hitRadiusWorld());
      c.cursor = 'pointer';
      c.on('pointertap', () => { onClickRef.current?.(liveId); });
      vp.addChild(c);
      fetchAndApplyLevel(liveId, c, color);
      return { sprite: c, trail };
    }

    function destroyLive(id: string) {
      const e = liveRef.current.get(id);
      if (!e) return;
      e.pc.dispose();
      e.trail.parent?.removeChild(e.trail);
      e.trail.destroy();
      e.sprite.parent?.removeChild(e.sprite);
      e.sprite.destroy();
      liveRef.current.delete(id);
    }

    function upsertLive(rid: string, nx: number, ny: number) {
      if (_fireflies.replays.has(rid)) {
        destroyFirefly(rid);
        _fireflies.replays.delete(rid);
      }

      const e = liveRef.current.get(rid);
      if (!e) {
        const color = getFireflyColor(rid);
        const result = makeLiveSprite(rid, color);
        if (!result) return;
        const { sprite, trail } = result;
        const { x, y } = normToWorld(nx, ny);
        sprite.position.set(x, y);
        const hist: Array<{x:number;y:number}> = [{ x, y }];
        const lastTrailT = 0;
        const pc = new PerfectCursor((pt: number[]) => {
          const cur = liveRef.current.get(rid);
          if (!cur) return;
          const w = normToWorld(pt[0], pt[1]);
          cur.sprite.position.set(w.x, w.y);
          const now = Date.now();
          if (now - cur.lastTrailT >= REPLAY_TICK_MS) {
            cur.lastTrailT = now;
            cur.hist.push({ x: w.x, y: w.y });
            if (cur.hist.length > TRAIL_LENGTH) cur.hist.shift();
            const lvl = _levelCache.get(rid) ?? 1;
            drawCometTail(cur.trail, cur.hist, color, FIREFLY_OUTER_R * levelToScale(lvl) * 0.28);
          }
        });
        pc.addPoint([nx, ny]);
        liveRef.current.set(rid, { sprite, pc, trail, hist, lastTrailT });
      } else {
        e.pc.addPoint([nx, ny]);
      }
    }

    // ── Hit area updater — keeps hitArea in screen-pixel terms as zoom changes ─
    // Runs every 200ms — cheap, no need for a zoom event listener
    const hitAreaTimer = setInterval(() => {
      const r = new PIXI.Circle(0, 0, hitRadiusWorld());
      for (const entry of liveRef.current.values()) {
        entry.sprite.hitArea = r;
      }
      for (const sprite of _fireflies.sprites.values()) {
        sprite.hitArea = r;
      }
    }, 200);

    // ── Live trail fade-out — clears stale trails when player stops moving ──
    const trailFadeTimer = setInterval(() => {
      const now = Date.now();
      for (const [rid, entry] of liveRef.current.entries()) {
        if (now - entry.lastTrailT < 200) continue; // still moving
        if (entry.hist.length === 0) continue;       // already empty
        // Remove 2 points per tick for a quick but smooth fade
        entry.hist.splice(0, 2);
        if (entry.hist.length < 2) {
          entry.trail.clear();
          entry.hist.length = 0;
        } else {
          const color = getFireflyColor(rid);
          const lvl = _levelCache.get(rid) ?? 1;
          drawCometTail(entry.trail, entry.hist, color, FIREFLY_OUTER_R * levelToScale(lvl) * 0.28);
        }
      }
    }, REPLAY_TICK_MS);

    // ── Resume firefly sprites from previous session (after navigation) ───
    let mountTimer: ReturnType<typeof setTimeout> | null = null;
    function tryMountFireflies() {
      if (!viewportRef.current) { mountTimer = setTimeout(tryMountFireflies, 50); return; }
      mountFireflySprites();
    }
    tryMountFireflies();

    // ── WebSocket ─────────────────────────────────────────────────────────
    const wsUrl = `${getWsBase()}/ws/map/${roomId}/${uid}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      if (wsGenRef.current !== gen) return;
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(ev.data as string); } catch { return; }

      const type = msg.type as string | undefined;

      if (type === 'welcome') {
        const welcomeUsers = Array.isArray(msg.users) ? msg.users : [];
        welcomeUsers.forEach((raw) => {
          const u = raw as { userId?: string | number; x?: number; y?: number };
          const rid = String(u.userId ?? '');
          if (rid && rid !== uid) upsertLive(rid, Number(u.x ?? 0.5), Number(u.y ?? 0.5));
        });
      } else if (type === 'user_online') {
        const oid = String(msg.userId ?? '');
        if (oid && oid !== uid) {
          destroyFirefly(oid);
          _fireflies.replays.delete(oid);
        }
      } else if (type === 'replay_start') {
        const fid = String(msg.userId ?? '');
        const recAt = msg.recorded_at != null ? Number(msg.recorded_at) : null;
        const elapsed = Number(msg.elapsed ?? 0);
        const started = fid && fid !== uid
          ? startReplay(fid, (msg.points as Pt[]) ?? [], elapsed, recAt)
          : false;
        if (started) {
          fireflyAckEndpointDebugFireflyAckPost({
            body: { room: roomId, myUid: uid, fireflyUid: fid, elapsed, synced: elapsed > 0, ts: Date.now() },
            throwOnError: false,
          }).catch(() => {/* ignore */});
        }
      } else if (type === 'replay_batch_end') {
        for (const fireflyId of Array.from(_fireflies.replays.keys())) {
          destroyFirefly(fireflyId);
          _fireflies.replays.delete(fireflyId);
        }
      } else if (type === 'live_sync') {
        // Authoritative live list from server — remove any cursor no longer live.
        // Fixes stale cursors caused by missed disconnect events in multi-worker setup.
        const liveUids = new Set(((msg.uids as string[]) ?? []).map(String));
        for (const id of Array.from(liveRef.current.keys())) {
          if (!liveUids.has(id)) destroyLive(id);
        }
      } else if (type === 'disconnect') {
        const did = String(msg.userId ?? '');
        destroyLive(did);
      } else {
        const rid = String(msg.userId ?? '');
        if (rid && rid !== uid) upsertLive(rid, Number(msg.x ?? 0.5), Number(msg.y ?? 0.5));
      }
    };

    ws.onopen = () => {
      if (wsGenRef.current !== gen) { ws.close(1000, 'stale'); return; }
      const vp2 = viewportRef.current;
      if (!vp2) return;
      const c  = vp2.center;
      const nx = Math.max(0, Math.min(1, (c.x + WORLD_SIZE / 2) / WORLD_SIZE));
      const ny = Math.max(0, Math.min(1, (c.y + WORLD_SIZE / 2) / WORLD_SIZE));
      lastSentRef.current = { nx, ny };
      ws.send(JSON.stringify({ x: nx, y: ny, emoji: '🔥', userId: uid }));
    };

    ws.onerror = () => {/* handled by onclose */};
    ws.onclose = () => {/* normal lifecycle */};

    return () => {
      if (mountTimer) clearTimeout(mountTimer);
      clearInterval(hitAreaTimer);
      clearInterval(trailFadeTimer);
      wsGenRef.current = gen + 1;
      ws.close(1000, 'cleanup');

      live.forEach(e => {
        e.pc.dispose();
        e.trail.clear();
        e.trail.parent?.removeChild(e.trail);
        e.trail.destroy();
        e.sprite.parent?.removeChild(e.sprite);
        e.sprite.destroy();
      });
      live.clear();

      _fireflies.sprites.forEach(s => { s.parent?.removeChild(s); s.destroy(); });
      _fireflies.sprites.clear();
      _fireflies.trails.forEach(t => { t.parent?.removeChild(t); t.destroy(); });
      _fireflies.trails.clear();
      _fireflies.histories.clear();
      _fireflies.intervals.forEach(iid => clearInterval(iid));
      _fireflies.intervals.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- WS lifecycle by user/room; viewport helpers via refs
  }, [userId, roomId]);

  // ── Send viewport center every 50ms ──────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      const vp = viewportRef.current;
      const ws = wsRef.current;
      if (!vp || !ws || ws.readyState !== WebSocket.OPEN) return;

      const c  = vp.center;
      const nx = (c.x + WORLD_SIZE / 2) / WORLD_SIZE;
      const ny = (c.y + WORLD_SIZE / 2) / WORLD_SIZE;

      if (
        Math.abs(nx - lastSentRef.current.nx) < MOVE_THRESHOLD &&
        Math.abs(ny - lastSentRef.current.ny) < MOVE_THRESHOLD
      ) return;

      lastSentRef.current = { nx, ny };
      ws.send(JSON.stringify({
        x: Math.max(0, Math.min(1, nx)),
        y: Math.max(0, Math.min(1, ny)),
        emoji: '🔥',
        userId: ws.url.split('/').pop() ?? '',
      }));
    }, SEND_INTERVAL_MS);
    return () => clearInterval(id);
  }, [viewportRef]);
}
